/**
 * Late-bound reference to the redux store, used to break circular imports.
 *
 * The cycle this exists to break:
 *
 *   store/store.ts -> rootReducer.ts -> <slice or service>.ts -> store/store.ts
 *
 * Several modules in rootReducer's transitive import tree need synchronous
 * access to the store (`store.getState()` / `store.dispatch(...)`). When
 * they `import store from 'src/store/store'` directly, the import closes
 * a cycle: store/store is mid-evaluating rootReducer when it re-enters
 * the same module, leaving its default export in TDZ. rspack's production
 * output (and recently dev too) throws "Cannot access
 * '__rspack_default_export' before initialization" on the first such
 * access.
 *
 * This module sits OUTSIDE that cycle: it has no imports from rootReducer's
 * tree. `store/store.ts` calls `setStore(store)` once after the store is
 * built; consumers call `getStore()` at runtime (well after module
 * evaluation finishes), so the binding is always populated when read.
 *
 * Use in any util/slice that's reachable from rootReducer:
 *
 *   import { getStore } from 'src/store/storeRef';
 *   ...
 *   const state = getStore().getState();
 *   getStore().dispatch(someAction);
 *
 * Files OUTSIDE rootReducer's tree (entry, components, hooks, tests) can
 * keep importing `store/store` directly — they don't close the cycle.
 */
import type storeDefaultExport from './store';

type Store = typeof storeDefaultExport;

let _store: Store | undefined;

export const setStore = (store: Store): void => {
  _store = store;
};

export const getStore = (): Store => {
  if (!_store) {
    throw new Error(
      'storeRef: getStore() called before the store was initialized. ' +
        'This typically means a module was evaluated during store creation ' +
        'and tried to access the store synchronously.',
    );
  }
  return _store;
};
